class PredictiveSearch extends HTMLElement {
    // Shared metaobject index cache (loaded once, reused across instances)
    static _moIndex = null;
    static _moLoading = null;

    constructor() {
      super();

      this.init()
      document.addEventListener('shopify:section:load', (event) => {
        if(event.target.closest('.shopify-section-header')) this.init()
      })
    }

    init() {
      this.modal = this.closest('.search-modal');
      this.cachedResults = {};
      this.input = this.querySelector('.search__input');
      this.loadingSpinner = this.querySelector('.loading-overlay__spinner')
      this.resetButton = this.querySelector('button[type="reset"]')
      this.clearText = this.querySelector('.search__button-text')
      if(!this.modal.className.includes('drawer')) {
        this.buttonClose = document.querySelector('.button-close--search')
        this.mobileButtonClose = document.querySelector('.header-segment-mobile .button-close--search')
      }
      this.overlay = document.querySelector('.search-modal__overlay')
      this.buttonClose = document.querySelector('.search-modal .button-close')
      this.header = document.querySelector('.header')
      this.searchForm = this.querySelector('form.search-modal__form')
      this.activeFilter = 'shop';
      this._supplementAbort = null;
      this._probeAbort = null;
      this._lastSupplementQuery = null;
      if(this.modal.closest('.header-search-style-field')) {
        if(this.modal.closest('search-modal')) {
          this.input = this.modal.closest('search-modal').querySelector('.search-field__text');
          this.loadingSpinner = this.modal.closest('search-modal').querySelector('.loading-overlay__spinner')
          this.resetButton = this.modal.closest('search-modal').querySelector('button[type="reset"]')
          this.clearText = this.modal.closest('search-modal').querySelector('.search__button-text')
          this.searchForm = this.modal.closest('search-modal').querySelector('form.search-modal__form')
        }
        if(document.querySelector('.template-search__search .search__input')) {
          let searchValue = document.querySelector('.template-search__search .search__input').value
          if (searchValue) this.input.value = ''
        }
      } else {
        document.addEventListener('searchmodal:open', () => {
          this.open()
          removeTrapFocus();
          if(this.modal.className.includes('drawer') && this.modal.className.includes('open')) {
            this.modal.querySelector('.search__input').readOnly = true;
            setTimeout(() => {
              this.modal.querySelector('.search__input').focus({ preventScroll: true })
              this.input.readOnly = false;
            })
          } else {
            this.input.focus()
          }
        })
      }
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.abortController = new AbortController();
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.searchForm.addEventListener('submit', this.onFormSubmit.bind(this));
      this.resetButton.addEventListener('click', this.clear.bind(this));
      if(this.buttonClose) this.buttonClose.addEventListener('click', this.clear.bind(this));
      if(this.mobileButtonClose) this.mobileButtonClose.addEventListener('click', this.clear.bind(this));
      this.header.addEventListener('click', (event) => {
        if(!event.target.closest('.search')) this.clear()
      });
      if(this.overlay) this.overlay.addEventListener('click', this.clear.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 200).bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      // Volume wraps the search input inside <summary>. Its onSummaryClick handler
      // calls close() on any click inside summary — meaning clicking the input
      // collapses the modal and wipes the query. Stop the click from bubbling
      // out of the search field so the input stays focusable.
      const fieldArea = this.input.closest('.search-field, .search-field__wrapper') || this.input.parentElement;
      if (fieldArea) {
        fieldArea.addEventListener('click', (event) => {
          // Close button still needs to bubble out so the modal close handler fires
          if (event.target.closest('.button-close')) return;
          event.stopPropagation();
        });
      }
    }

    buttonClick(event) {
      if((window.innerWidth <= 1024 && document.querySelector('.header-segment-mobile .search').closest('.search--field')) || (window.innerWidth > 1024 && document.querySelector('.header-segment-desktop .search').closest('.search--field'))) {
        event.preventDefault()
        let headerform
        if(window.innerWidth <= 1024 && document.querySelector('.header-segment-mobile .search').closest('.search--field')) headerform = document.querySelector('.header-segment-mobile .search').querySelector('.search-modal__form')
        if(window.innerWidth > 1024 && document.querySelector('.header-segment-desktop .search').closest('.search--field')) headerform = document.querySelector('.header-segment-desktop .search').querySelector('.search-modal__form')
        headerform.submit()
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();
      if (searchTerm.length === 0) {
        this.clear();
        this.showZeroState();
        return;
      }
      this.hideZeroState();
      this.getSearchResults(searchTerm)
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFocus() {
      if(this.input.closest('.focused')) return
      document.body.classList.add('predictive-search--focus');
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.showZeroState();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(searchTerm);
      }
    }

    // ==========================================
    // ZERO-STATE: Recently Viewed + Popular
    // ==========================================

    showZeroState() {
      const recentlyViewedContainer = this.closest('.search-modal')?.querySelector('.search-recently-viewed');
      if (recentlyViewedContainer) {
        this.loadRecentlyViewed(recentlyViewedContainer);
      }
    }

    hideZeroState() {
      const recentlyViewedContainer = this.closest('.search-modal')?.querySelector('.search-recently-viewed');
      if (recentlyViewedContainer) {
        recentlyViewedContainer.classList.add('hidden');
      }
    }

    loadRecentlyViewed(container) {
      const cookieName = 'volume-theme:recently-viewed';
      const items = JSON.parse(localStorage.getItem(cookieName) || '[]').filter(Boolean);
      const grid = container.querySelector('.search-recently-viewed__grid');
      if (!grid || items.length === 0) {
        container.classList.add('hidden');
        return;
      }

      if (grid.children.length > 0) {
        container.classList.remove('hidden');
        return;
      }

      const productIds = items.slice(0, 6);
      const queryString = productIds.map(id => 'id:' + id).join(' OR ');

      // Use section rendering to get theme-consistent card-product cards
      fetch(`/search?q=${encodeURIComponent(queryString)}&type=product&section_id=predictive-search`)
        .then(r => r.text())
        .then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const productItems = doc.querySelectorAll('#content-predictive-search-products .predictive-search__list-item');

          if (productItems.length === 0) {
            container.classList.add('hidden');
            return;
          }

          container.classList.remove('hidden');
          grid.innerHTML = '';
          productItems.forEach(li => {
            const card = li.cloneNode(true);
            card.removeAttribute('id');
            card.removeAttribute('aria-selected');
            card.classList.add('search-rv__card');
            grid.appendChild(card);
          });
        })
        .catch(() => {
          container.classList.add('hidden');
        });
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    // Extract product type from a card DOM node (checks for portrait class or data attribute)
    _detectProductType(node) {
      // Check if the source node already has data-product-type
      var existing = node.getAttribute ? node.getAttribute('data-product-type') : null;
      if (existing) return existing;
      // Detect Screen Printed Poster via the portrait class added by card-product.liquid
      if (node.querySelector && node.querySelector('.card__product-media--portrait')) {
        return 'Screen Printed Poster';
      }
      return null;
    }

    // ==========================================
    // FUZZY MATCHING - Typo Tolerance
    // ==========================================

    fuzzyCorrect(term) {
      const corrections = {
        'vynil': 'vinyl', 'vinal': 'vinyl', 'vinly': 'vinyl', 'vynl': 'vinyl',
        'casette': 'cassette', 'cassete': 'cassette', 'casete': 'cassette',
        'tshirt': 't-shirt', 'tee shirt': 't-shirt', 'teeshirt': 't-shirt',
        'hoodie': 'hoodie', 'hoody': 'hoodie', 'hoddie': 'hoodie',
        'levitaton': 'levitation', 'levitaiton': 'levitation', 'levitatoin': 'levitation',
        'psychadelic': 'psychedelic', 'psycadelic': 'psychedelic', 'pyschedelic': 'psychedelic', 'psyhedelic': 'psychedelic',
        'gizzard': 'gizzard', 'gizard': 'gizzard', 'gizzerd': 'gizzard',
        'reverb': 'reverberation', 'reverbaration': 'reverberation',
        'preoder': 'pre-order', 'pre order': 'pre-order', 'preorder': 'pre-order',
        'merchanise': 'merch', 'merchandise': 'merch',
        'cd': 'cd', 'compact disc': 'cd',
        'lp': 'vinyl records', 'record': 'vinyl records', 'records': 'vinyl records',
        'cloths': 'apparel', 'clothes': 'apparel', 'clothing': 'apparel',
        'poster': 'poster', 'postr': 'poster',
        'stiker': 'sticker', 'sticekr': 'sticker',
      };

      const lowerTerm = term.toLowerCase();

      if (corrections[lowerTerm]) {
        return { corrected: corrections[lowerTerm], original: term };
      }

      // Levenshtein distance check for close matches
      for (const [typo, fix] of Object.entries(corrections)) {
        if (this.levenshtein(lowerTerm, typo) <= 1) {
          return { corrected: fix, original: term };
        }
      }

      return null;
    }

    levenshtein(a, b) {
      if (a.length === 0) return b.length;
      if (b.length === 0) return a.length;
      const matrix = [];
      for (let i = 0; i <= b.length; i++) matrix[i] = [i];
      for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
      for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
          const cost = b.charAt(i - 1) === a.charAt(j - 1) ? 0 : 1;
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j - 1] + cost
          );
        }
      }
      return matrix[b.length][a.length];
    }

    // ==========================================
    // SEARCH ANALYTICS - Track Queries
    // ==========================================

    trackSearch(term, resultCount) {
      try {
        const storageKey = 'levitation-search-analytics';
        const analytics = JSON.parse(localStorage.getItem(storageKey) || '[]');

        analytics.unshift({
          query: term,
          results: resultCount,
          timestamp: Date.now()
        });

        // Keep last 100 searches, expire after 14 days
        const twoWeeksAgo = Date.now() - (14 * 24 * 60 * 60 * 1000);
        const filtered = analytics
          .filter(a => a.timestamp > twoWeeksAgo)
          .slice(0, 100);

        localStorage.setItem(storageKey, JSON.stringify(filtered));

        // Track zero-result queries separately for merchandising insights
        if (resultCount === 0) {
          const noResultsKey = 'levitation-search-no-results';
          const noResults = JSON.parse(localStorage.getItem(noResultsKey) || '[]');
          noResults.unshift({ query: term, timestamp: Date.now() });
          localStorage.setItem(noResultsKey, JSON.stringify(noResults.slice(0, 30)));
        }
      } catch(e) {
        // localStorage may be full or unavailable
      }
    }

    // ==========================================
    // SEARCH FILTER TABS
    // ==========================================

    static FILTER_TYPES = {
      vinyl: ['Vinyl Records', 'Cassettes', 'DVD', 'Digital'],
      poster: ['Screen Printed Poster']
    };

    // Unified filter row → panel mapping. Render order in the Liquid is:
    //   Shop | Read | Watch | Vinyl | Events | Poster | Pages | Suggestions | Artists
    static PANEL_MAP = {
      shop: '#content-predictive-search-products',
      read: '#content-predictive-search-articles',
      watch: '#content-predictive-search-watch',
      artists: '#content-predictive-search-artists',
      vinyl: '#content-predictive-search-products',
      events: '#content-predictive-search-presents',
      poster: '#content-predictive-search-products',
      pages: '#content-predictive-search-pages',
      suggestions: '#content-predictive-search-queries'
    };

    initFilterTabs() {
      const filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (!filterBar) return;
      if (this._filterClickHandler) filterBar.removeEventListener('click', this._filterClickHandler);

      this._filterClickHandler = (e) => {
        const tab = e.target.closest('[data-filter]');
        if (!tab || tab.classList.contains('search-filter-tab--hidden')) return;
        e.stopPropagation();

        const filter = tab.dataset.filter;
        filterBar.querySelectorAll('[data-filter]').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        this.activeFilter = filter;

        const query = this.getQuery();
        if (!query) return;

        const panelSelector = PredictiveSearch.PANEL_MAP[filter];
        if (!panelSelector) return;

        if (filter === 'shop') {
          this._restoreAllResults();
          this._showOnlyPanel(panelSelector);
        } else if (filter === 'vinyl' || filter === 'poster') {
          // _fetchFilteredSection rewrites the products panel + activates it
          this._fetchFilteredSection(query, filter);
          this._showOnlyPanel(panelSelector);
        } else {
          this._showOnlyPanel(panelSelector);
        }
      };
      filterBar.addEventListener('click', this._filterClickHandler);

      this._probeFilterCounts();
    }

    // Show one tab-content-js panel, hide the rest
    _showOnlyPanel(selector) {
      this.predictiveSearchResults.querySelectorAll('.tab-content-js').forEach(c => c.classList.remove('active'));
      const panel = this.predictiveSearchResults.querySelector(selector);
      if (panel) panel.classList.add('active');
    }

    _setTabVisibility(tab, visible) {
      if (!tab) return;
      if (visible) {
        tab.classList.remove('search-filter-tab--hidden');
        tab.style.display = '';
      } else {
        tab.classList.add('search-filter-tab--hidden');
        tab.style.display = 'none';
      }
    }

    _updateFilterBarVisibility() {
      const filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (!filterBar) return;
      const visibleTabs = filterBar.querySelectorAll('[data-filter]:not(.search-filter-tab--hidden)');
      filterBar.style.display = visibleTabs.length === 0 ? 'none' : '';
    }

    _probeFilterCounts() {
      const filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (!filterBar) return;

      // Static buttons: check if the matching server-rendered panel has any <li>
      const staticChecks = {
        shop: this.predictiveSearchResults.querySelector('#content-predictive-search-products'),
        read: this.predictiveSearchResults.querySelector('#content-predictive-search-articles'),
        pages: this.predictiveSearchResults.querySelector('#content-predictive-search-pages'),
        suggestions: this.predictiveSearchResults.querySelector('#content-predictive-search-queries')
      };
      Object.entries(staticChecks).forEach(([filter, panel]) => {
        const tab = filterBar.querySelector(`[data-filter="${filter}"]`);
        const hasItems = !!(panel && panel.querySelector('li'));
        this._setTabVisibility(tab, hasItems);
      });

      // Watch / Artists / Events start hidden — their inject functions reveal them once panels land
      const watchPanel = this.predictiveSearchResults.querySelector('#content-predictive-search-watch');
      this._setTabVisibility(filterBar.querySelector('[data-filter="watch"]'), !!(watchPanel && watchPanel.querySelector('li')));
      const artistsPanel = this.predictiveSearchResults.querySelector('#content-predictive-search-artists');
      this._setTabVisibility(filterBar.querySelector('[data-filter="artists"]'), !!(artistsPanel && artistsPanel.querySelector('li')));
      const presentsPanel = this.predictiveSearchResults.querySelector('#content-predictive-search-presents');
      this._setTabVisibility(filterBar.querySelector('[data-filter="events"]'), !!(presentsPanel && presentsPanel.querySelector('li')));

      // Vinyl + Poster: probe via search API (unknown count from unfiltered render)
      const query = this.getQuery();
      if (query) {
        if (this._probeAbort) this._probeAbort.abort();
        this._probeAbort = new AbortController();
        const signal = this._probeAbort.signal;
        const self = this;
        const probeProductFilter = function(filterKey) {
          const types = PredictiveSearch.FILTER_TYPES[filterKey];
          if (!types) return;
          const typeQuery = types.map(t => 'product_type:"' + t + '"').join(' OR ');
          const fullQuery = query + ' (' + typeQuery + ')';
          fetch('/search/suggest.json?q=' + encodeURIComponent(fullQuery) + '&resources[type]=product&resources[limit]=1', { signal })
            .then(r => r.json())
            .then(d => {
              const count = (d.resources && d.resources.results && d.resources.results.products) ? d.resources.results.products.length : 0;
              self._setTabVisibility(filterBar.querySelector('[data-filter="' + filterKey + '"]'), count > 0);
              self._updateFilterBarVisibility();
            })
            .catch(() => {});
        };
        probeProductFilter('vinyl');
        probeProductFilter('poster');
      }

      this._updateFilterBarVisibility();
    }

    _restoreAllResults() {
      const productList = this.predictiveSearchResults.querySelector('.predictive-search__results-list');
      if (productList && this._cachedAllHTML && this._cachedAllQuery === this.getQuery()) {
        productList.innerHTML = this._cachedAllHTML;
      } else {
        this.getSearchResults(this.getQuery());
        return;
      }
      const productsContent = this.predictiveSearchResults.querySelector('#content-predictive-search-products');
      if (productsContent) productsContent.classList.add('active');
      // Re-bind filter click handler without re-probing
      var filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (filterBar) {
        if (this._filterClickHandler) filterBar.removeEventListener('click', this._filterClickHandler);
        filterBar.addEventListener('click', this._filterClickHandler);
      }
    }

    _fetchFilteredSection(query, filter) {
      this.setLiveRegionLoadingState();
      this.abortController.abort();
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      const types = PredictiveSearch.FILTER_TYPES[filter];
      if (!types || types.length === 0) return;

      // Build product_type filter params for the full search page
      const typeParams = types.map(t => `filter.p.product_type=${encodeURIComponent(t)}`).join('&');

      // Use main-search section rendering (returns full theme cards, no 10-item cap)
      fetch(`/search?q=${encodeURIComponent(query)}&type=product&${typeParams}&section_id=main-search`, { signal })
        .then(r => r.text())
        .then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          // main-search wraps cards in LI.grid__item - extract those directly
          const gridItems = doc.querySelectorAll('.grid__item');
          let items;
          if (gridItems.length > 0) {
            items = Array.from(gridItems).map(gi => {
              const li = document.createElement('li');
              li.className = 'predictive-search__list-item grid-item';
              li.setAttribute('role', 'option');
              li.innerHTML = gi.innerHTML;
              var ptype = this._detectProductType(gi);
              if (ptype) li.setAttribute('data-product-type', ptype);
              return li;
            });
          } else {
            // Fallback: extract card-product directly
            items = Array.from(doc.querySelectorAll('.card-product')).map(card => {
              const li = document.createElement('li');
              li.className = 'predictive-search__list-item grid-item';
              li.setAttribute('role', 'option');
              li.appendChild(card.cloneNode(true));
              var ptype = this._detectProductType(li);
              if (ptype) li.setAttribute('data-product-type', ptype);
              return li;
            });
          }
          // Dedupe by href
          const seen = new Set();
          const uniqueProducts = items.filter(li => {
            const href = li.querySelector('a')?.getAttribute('href') || '';
            if (seen.has(href)) return false;
            seen.add(href);
            return true;
          });
          this._renderFilteredSection(uniqueProducts, filter);
        })
        .catch(error => {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.loadingSpinner.classList.add('hidden');
          this.removeAttribute('loading');
        });
    }

    _renderFilteredSection(productItems, filter) {
      const productList = this.predictiveSearchResults.querySelector('#content-predictive-search-products .predictive-search__results-list');
      if (!productList) return;

      const allItems = productItems;

      if (allItems.length === 0) {
        const safeQuery = (this.getQuery() || '').replace(/</g, '&lt;').replace(/"/g, '&quot;');
        productList.innerHTML = `<li class="predictive-search__no-results" style="padding:24px;text-align:center;grid-column:1/-1;">
          <p>No ${filter} found for "${safeQuery}"</p>
          <p style="margin-top:8px;"><a href="/search?q=${encodeURIComponent(this.getQuery())}&type=product" class="link">View all search results</a></p>
        </li>`;
      } else {
        // Inject server-rendered DOM nodes directly (preserves all theme classes and styling)
        productList.innerHTML = '';
        allItems.forEach(li => productList.appendChild(li));
      }

      // Ensure products tab content is visible
      const productsContent = this.predictiveSearchResults.querySelector('#content-predictive-search-products');
      if (productsContent) productsContent.classList.add('active');

      // Set active filter tab
      const filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (filterBar) {
        filterBar.querySelectorAll('[data-filter]').forEach(t => t.classList.remove('active'));
        const activeTab = filterBar.querySelector(`[data-filter="${filter}"]`);
        if (activeTab) activeTab.classList.add('active');
      }

      this.loadingSpinner.classList.add('hidden');
      this.removeAttribute('loading');
      // Re-bind click handler only (no re-probe)
      if (filterBar) {
        if (this._filterClickHandler) filterBar.removeEventListener('click', this._filterClickHandler);
        filterBar.addEventListener('click', this._filterClickHandler);
      }
    }

    // ==========================================
    // CORE SEARCH LOGIC
    // ==========================================

    onKeyup(event) {
      if (!this.getQuery().length) this.clear(event);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up')
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      if (
        event.code === 'ArrowUp' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
      // Only navigate within the active tab's list items
      const activeTab = this.predictiveSearchResults.querySelector('.tab-content-js.active');
      const allElements = activeTab ? activeTab.querySelectorAll('li[role="option"]') : this.querySelectorAll('li[role="option"]');
      if (allElements.length === 0) return; // active tab has no items — nothing to navigate
      let activeElement = allElements[0];

      if (moveUp && !selectedElement) return;

      if (this.statusElement) this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElement = selectedElement.nextElementSibling || allElements[0];
      } else if (moveUp) {
        activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
      }

      if (activeElement === selectedElement) return;

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.setLiveRegionText(activeElement.textContent);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
      if (selectedProduct) selectedProduct.click();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        if (this.modal) this.modal.classList.add('searching');
        return;
      }

      // Abort previous request
      this.abortController.abort();
      this.abortController = new AbortController();

      fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`, {
        signal: this.abortController.signal,
        })
      .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);

          // Track search analytics
          const resultCount = (resultsMarkup.match(/predictive-search__list-item/g) || []).length;
          this.trackSearch(searchTerm, resultCount);

          // If no results, try fuzzy correction
          if (resultCount === 0) {
            this.handleNoResults(searchTerm);
          }

          if (this.modal) {
            setTimeout(() => {
              this.modal.classList.add('searching')
            }, 100)
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') throw error;
        });
    }

    // ==========================================
    // NO-RESULTS STATE
    // ==========================================

    handleNoResults(searchTerm) {
      const correction = this.fuzzyCorrect(searchTerm);
      const noResultsEl = this.predictiveSearchResults.querySelector('.predictive-search-results-empty');

      if (!noResultsEl) return;

      // Clear any previous suggestions before adding new ones
      var existing = noResultsEl.querySelector('.search-no-results');
      if (existing) existing.remove();

      let suggestionsHtml = '<div class="search-no-results">';

      if (correction) {
        suggestionsHtml += `
          <p class="search-no-results__correction">
            Did you mean <a href="#" class="search-correction-link" data-correction="${this.escapeHtml(correction.corrected)}">"${this.escapeHtml(correction.corrected)}"</a>?
          </p>`;
      }

      suggestionsHtml += `
        <div class="search-no-results__suggestions">
          <p class="search-no-results__heading">Try searching for:</p>
          <div class="search-no-results__links">
            <a href="/search?q=vinyl+records" class="search-suggestion-pill">Vinyl</a>
            <a href="/search?q=t-shirt" class="search-suggestion-pill">T-Shirts</a>
            <a href="/search?q=levitation" class="search-suggestion-pill">Levitation</a>
            <a href="/search?q=pre-order" class="search-suggestion-pill">Pre-Orders</a>
            <a href="/search?q=new+release" class="search-suggestion-pill">New Releases</a>
            <a href="/collections/all" class="search-suggestion-pill">Browse All</a>
          </div>
        </div>
      </div>`;

      noResultsEl.classList.remove('visually-hidden');
      // Use insertAdjacentHTML to append without destroying existing children
      noResultsEl.insertAdjacentHTML('beforeend', suggestionsHtml);

      // Bind correction link
      const correctionLink = noResultsEl.querySelector('.search-correction-link');
      if (correctionLink) {
        correctionLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.input.value = correctionLink.dataset.correction;
          this.onChange();
        });
      }
    }

    setLiveRegionLoadingState() {
      this.loadingSpinner.classList.remove('hidden')
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
      this.clearText.classList.add('visually-hidden')
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      // Abort any pending graceful-close fade so a fresh render isn't faded out
      if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
      this.predictiveSearchResults.classList.remove('is-closing');
      this.predictiveSearchResults.classList.remove('visually-hidden')
      this.setLiveRegionResults();
      this.open();
      // Show clear button when there's input, hide when empty
      var clearBtn = this.input.closest('form') ? this.input.closest('form').querySelector('.search__button') : null;
      if (clearBtn) {
        clearBtn.setAttribute('tabindex', this.input.value !== '' ? '0' : '-1');
      }
      this.predictiveSearchButton = this.querySelector('.predictive-search__button')
      this.predictiveSearchButton?.addEventListener('click', this.buttonClick.bind(this))

      // Initialize filter tabs after rendering
      this.initFilterTabs();
      this.activeFilter = 'shop';

      // Ensure Products tab is active and visible (component-tabs may hide it)
      this.activateProductsTab();

      // Supplement with more products from full search (predictive API caps at 10)
      this._supplementResults();

      // Search metaobjects and inject Watch / Artists / Events panels separately
      this._loadMetaobjectIndex().then(function(index) {
        if (!index) return;
        var q = this.getQuery();
        if (!q) return;
        var results = this._searchMetaobjects(q);
        this._injectWatchResults(results.videos || []);
        this._injectArtistsResults(results.artists || []);
        this._injectPresentsResults(results.presents || []);
      }.bind(this));
    }

    _supplementResults() {
      const query = this.getQuery();
      if (!query) return;
      // Skip if we already supplemented this exact query
      if (this._lastSupplementQuery === query) {
        this._cachedAllHTML = this.predictiveSearchResults.querySelector('.predictive-search__results-list')?.innerHTML;
        this._cachedAllQuery = query;
        return;
      }
      this._lastSupplementQuery = query;

      const productList = this.predictiveSearchResults.querySelector('.predictive-search__results-list');
      if (!productList) return;

      // Get URLs already shown from predictive search
      const existingUrls = new Set();
      productList.querySelectorAll('a[href]').forEach(a => existingUrls.add(a.getAttribute('href')));

      // Abort any previous supplement fetch
      if (this._supplementAbort) this._supplementAbort.abort();
      this._supplementAbort = new AbortController();

      // Fetch full search results for more products
      fetch(`/search?q=${encodeURIComponent(query)}&type=product&section_id=main-search`, { signal: this._supplementAbort.signal })
        .then(r => r.text())
        .then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const extras = Array.from(doc.querySelectorAll('.grid__item')).filter(gi => {
            const href = gi.querySelector('a')?.getAttribute('href') || '';
            return href && !existingUrls.has(href);
          });

          // Append new products to the list
          extras.forEach(gi => {
            const li = document.createElement('li');
            li.className = 'predictive-search__list-item grid-item';
            li.setAttribute('role', 'option');
            li.innerHTML = gi.innerHTML;
            var ptype = this._detectProductType(gi);
            if (ptype) li.setAttribute('data-product-type', ptype);
            productList.appendChild(li);
          });

          // Now cache the complete list (original + supplemented)
          this._cachedAllHTML = productList.innerHTML;
          this._cachedAllQuery = query;
        })
        .catch(() => {
          // Cache whatever we have even if supplement fails
          this._cachedAllHTML = productList.innerHTML;
          this._cachedAllQuery = query;
        });
    }

    activateProductsTab() {
      const productsTabBtn = this.predictiveSearchResults.querySelector('#predictive-search-products');
      const productsContent = this.predictiveSearchResults.querySelector('#content-predictive-search-products');

      // Activate the Products tab button
      if (productsTabBtn) {
        this.predictiveSearchResults.querySelectorAll('.tab-js').forEach(t => t.classList.remove('active'));
        productsTabBtn.classList.add('active');
      }

      // Show Products content via .active class (CSS uses .active to control visibility)
      this.predictiveSearchResults.querySelectorAll('.tab-content-js').forEach(c => c.classList.remove('active'));
      if (productsContent) productsContent.classList.add('active');
    }

    setLiveRegionResults() {
      this.loadingSpinner.classList.add('hidden')
      this.removeAttribute('loading');
      this.clearText.classList.remove('visually-hidden')
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      document.body.classList.add('predictive-search--focus');
    }

    clear(event) {
      if(event) event.preventDefault();
      this.clearText.classList.add('visually-hidden')
      this.input.value = '';
      if(this.input.closest('form').querySelector('.search__button')) {
        this.input.closest('form').querySelector('.search__button').setAttribute('tabindex', '-1')
      }
      this.removeAttribute('open');
      this.removeAttribute('results');

      // Graceful close: fade the panel out via .is-closing (CSS-animated),
      // then apply the hard .visually-hidden after the transition completes.
      if (this._closeTimer) clearTimeout(this._closeTimer);
      this.predictiveSearchResults.classList.add('is-closing');
      this._closeTimer = setTimeout(() => {
        this.predictiveSearchResults.classList.add('visually-hidden');
        this.predictiveSearchResults.classList.remove('is-closing');
        this._closeTimer = null;
      }, 240);

      if(event && event.target.closest('.search__button')?.querySelector('.search__button-text')) {
        trapFocus(this.input);
        setTimeout( () => this.input.focus(), 100)
      }

      if (this.modal) this.modal.classList.remove('searching');
      // Reset supplement tracking so next search re-fetches
      this._lastSupplementQuery = null;
    }
    // ==========================================
    // METAOBJECT SEARCH (Videos, Series, Artists)
    // ==========================================

    _loadMetaobjectIndex() {
      if (PredictiveSearch._moIndex) return Promise.resolve(PredictiveSearch._moIndex);
      if (PredictiveSearch._moLoading) return PredictiveSearch._moLoading;

      var self = this;
      PredictiveSearch._moLoading = Promise.all([
        // Videos + presents + video-derived artists from Liquid section
        fetch('/?section_id=metaobject-search-index')
          .then(function(r) { return r.text(); })
          .then(function(text) {
            var div = document.createElement('div');
            div.innerHTML = text;
            try { return JSON.parse(div.textContent.trim()); }
            catch(e) { return { videos: [], artists: [], presents: [] }; }
          }),
        // All artists via Storefront API (paginates past Liquid's 50-item cap)
        self._fetchAllArtistsFromStorefront()
      ])
      .then(function(results) {
        var liquidData = results[0];
        var sfArtists = results[1];
        // Dedupe artists: Liquid (video-derived, has v:true) first, then Storefront fills the rest
        var seenHandles = {};
        var allArtists = [];
        (liquidData.artists || []).forEach(function(a) {
          if (!seenHandles[a.h]) { seenHandles[a.h] = true; allArtists.push(a); }
        });
        sfArtists.forEach(function(a) {
          if (!seenHandles[a.h]) { seenHandles[a.h] = true; allArtists.push(a); }
        });
        var index = {
          videos: liquidData.videos || [],
          artists: allArtists,
          presents: liquidData.presents || []
        };
        PredictiveSearch._moIndex = index;
        return index;
      })
      .catch(function(err) {
        console.warn('Failed to load metaobject search index:', err);
        PredictiveSearch._moLoading = null;
        return null;
      });

      return PredictiveSearch._moLoading;
    }

    _fetchAllArtistsFromStorefront() {
      var token = '4d61c55982ad8bca9301276e9797fffa';
      var endpoint = 'https://shop-levitation.myshopify.com/api/2025-01/graphql.json';
      var allArtists = [];

      function fetchPage(cursor) {
        var afterClause = cursor ? ', after: "' + cursor + '"' : '';
        var query = '{ metaobjects(type: "lineup_artist", first: 250' + afterClause + ') { nodes { handle field(key: "name") { value } img: field(key: "image") { reference { ... on MediaImage { image { url(transform: { maxWidth: 200 }) } } } } } pageInfo { hasNextPage endCursor } } }';

        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({ query: query })
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
          var nodes = json.data && json.data.metaobjects ? json.data.metaobjects.nodes : [];
          nodes.forEach(function(n) {
            var imgUrl = '';
            if (n.img && n.img.reference && n.img.reference.image) {
              imgUrl = n.img.reference.image.url;
            }
            allArtists.push({
              h: n.handle,
              n: n.field ? n.field.value : '',
              i: imgUrl,
              v: false
            });
          });
          var pageInfo = json.data && json.data.metaobjects ? json.data.metaobjects.pageInfo : {};
          if (pageInfo.hasNextPage) {
            return fetchPage(pageInfo.endCursor);
          }
          return allArtists;
        });
      }

      return fetchPage(null);
    }

    _searchMetaobjects(term) {
      var index = PredictiveSearch._moIndex;
      if (!index) return { videos: [], artists: [], presents: [] };

      var lower = term.toLowerCase();
      var words = lower.split(/\s+/).filter(Boolean);
      // Strip common prefixes for fuzzy band name matching
      var coreTerm = lower.replace(/^(the|thee)\s+/, '').trim();

      function matchScore(text) {
        if (!text) return 0;
        var tl = text.toLowerCase();
        var coreText = tl.replace(/^(the|thee)\s+/, '').trim();
        if (tl === lower || coreText === coreTerm) return 100;
        if (tl.startsWith(lower) || coreText.startsWith(coreTerm)) return 90;
        if (tl.includes(lower) || coreText.includes(coreTerm)) return 80;
        var wordScore = 0;
        words.forEach(function(w) { if (tl.includes(w)) wordScore += 30; });
        return Math.min(wordScore, 70);
      }

      var videos = (index.videos || [])
        .map(function(v) {
          var s = Math.max(matchScore(v.a), matchScore(v.sn), matchScore(v.t), matchScore(v.sr));
          return { item: v, score: s };
        })
        .filter(function(v) { return v.score > 0; })
        .sort(function(a, b) { return b.score - a.score; })
        .slice(0, 8)
        .map(function(v) { return v.item; });

      var artists = (index.artists || [])
        .map(function(a) {
          return { item: a, score: matchScore(a.n) };
        })
        .filter(function(a) { return a.score > 0; })
        .sort(function(a, b) { return b.score - a.score; })
        .slice(0, 16)
        .map(function(a) { return a.item; });

      // Presents: match title, venue, city, partner. Sort by date (upcoming first).
      var todayIso = new Date().toISOString().slice(0, 10);
      var presents = (index.presents || [])
        .map(function(p) {
          var s = Math.max(matchScore(p.t), matchScore(p.v), matchScore(p.c), matchScore(p.p));
          return { item: p, score: s };
        })
        .filter(function(p) { return p.score > 0; })
        .sort(function(a, b) {
          // Upcoming events first, then by score
          var aFuture = (a.item.d || '') >= todayIso ? 1 : 0;
          var bFuture = (b.item.d || '') >= todayIso ? 1 : 0;
          if (aFuture !== bFuture) return bFuture - aFuture;
          if (aFuture) return (a.item.d || '').localeCompare(b.item.d || '');
          return b.score - a.score;
        })
        .slice(0, 8)
        .map(function(p) { return p.item; });

      return { videos: videos, artists: artists, presents: presents };
    }

    // Watch panel — videos only (artists has its own panel; series is not surfaced in search)
    _injectWatchResults(videos) {
      var contentArea = this.predictiveSearchResults.querySelector('.predictive-search-results-content');
      if (!contentArea) return;

      var oldContent = contentArea.querySelector('#content-predictive-search-watch');
      if (oldContent) oldContent.remove();
      if (!videos || videos.length === 0) return;

      var self = this;
      var html = '<div id="content-predictive-search-watch" class="tab-content-js component-tabs__content">';
      html += '<ul class="predictive-search__results-list ps-card-grid" role="listbox">';

      videos.forEach(function(v) {
        var thumb = v.th || '';
        var typeLabel = v.tp === 'full_set' ? 'Full Set' : (v.tp === 'trailer' ? 'Trailer' : '');
        html += '<li class="predictive-search__list-item ps-card ps-card--video" role="option" aria-selected="false">';
        html += '<a href="/pages/watch/' + v.h + '" class="ps-card__link" tabindex="-1">';
        html += '<div class="ps-card__media ps-card__media--video">';
        if (thumb) html += '<img src="' + thumb + '" alt="' + self.escapeHtml(v.sn || v.t) + '" loading="lazy" onerror="this.onerror=null;this.src=this.src.replace(\'maxresdefault\',\'mqdefault\');">';
        if (typeLabel) html += '<span class="ps-card__type caption-font">' + typeLabel + '</span>';
        if (v.d) html += '<span class="ps-card__dur">' + v.d + '</span>';
        html += '</div>';
        html += '<div class="ps-card__body">';
        if (v.sr) html += '<div class="ps-card__subtitle caption-font">' + self.escapeHtml(v.sr) + '</div>';
        html += '<h3 class="ps-card__title body-font">' + self.escapeHtml(v.sn || v.t) + '</h3>';
        if (v.a) html += '<div class="ps-card__meta caption-font">' + self.escapeHtml(v.a) + '</div>';
        html += '</div></a></li>';
      });

      html += '</ul></div>';
      contentArea.insertAdjacentHTML('beforeend', html);

      var filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (filterBar) {
        var watchTab = filterBar.querySelector('[data-filter="watch"]');
        if (watchTab) {
          watchTab.classList.remove('search-filter-tab--hidden');
          watchTab.style.display = '';
        }
        this._updateFilterBarVisibility();
      }
    }

    // Artists panel — compact list (small circular thumb + name), 2-col grid on desktop
    _injectArtistsResults(artists) {
      var contentArea = this.predictiveSearchResults.querySelector('.predictive-search-results-content');
      if (!contentArea) return;

      var oldContent = contentArea.querySelector('#content-predictive-search-artists');
      if (oldContent) oldContent.remove();
      if (!artists || artists.length === 0) return;

      var self = this;
      var html = '<div id="content-predictive-search-artists" class="tab-content-js component-tabs__content">';
      html += '<ul class="predictive-search__results-list ps-list" role="listbox">';

      artists.forEach(function(a) {
        html += '<li class="predictive-search__list-item ps-list__item" role="option" aria-selected="false">';
        html += '<a href="/search?q=%2A&type=product&filter.p.m.music.artist=' + encodeURIComponent(a.n) + '" class="ps-list__link" tabindex="-1">';
        html += '<div class="ps-list__thumb ps-list__thumb--artist">';
        if (a.i) {
          html += '<img src="' + a.i + '" alt="' + self.escapeHtml(a.n) + '" loading="lazy">';
        }
        html += '</div>';
        html += '<div class="ps-list__body">';
        html += '<h3 class="ps-list__title body-font">' + self.escapeHtml(a.n) + '</h3>';
        if (a.v) html += '<p class="ps-list__meta caption-font">Videos available</p>';
        html += '</div></a></li>';
      });

      html += '</ul></div>';
      contentArea.insertAdjacentHTML('beforeend', html);

      var filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (filterBar) {
        var artistsTab = filterBar.querySelector('[data-filter="artists"]');
        if (artistsTab) {
          artistsTab.classList.remove('search-filter-tab--hidden');
          artistsTab.style.display = '';
        }
        this._updateFilterBarVisibility();
      }
    }

    _injectPresentsResults(presents) {
      var contentArea = this.predictiveSearchResults.querySelector('.predictive-search-results-content');
      if (!contentArea) return;

      var oldContent = contentArea.querySelector('#content-predictive-search-presents');
      if (oldContent) oldContent.remove();
      if (!presents || presents.length === 0) return;

      var self = this;
      var html = '<div id="content-predictive-search-presents" class="tab-content-js component-tabs__content">';
      html += '<ul class="predictive-search__results-list ps-card-grid" role="listbox">';

      // Parse the title into "Presents:" subtitle + artist/title head, mirroring lpg-card
      function splitTitle(full) {
        if (!full) return { subtitle: '', title: '' };
        var idx = full.indexOf(':');
        if (idx > -1 && idx < full.length - 1) {
          return {
            subtitle: full.substring(0, idx + 1).trim(),
            title: full.substring(idx + 1).trim()
          };
        }
        return { subtitle: 'Levitation Presents', title: full };
      }

      presents.forEach(function(ev) {
        var split = splitTitle(ev.t);
        var venueCity = [ev.v, ev.c].filter(Boolean).join(', ');
        var statusBadge = '';
        if (ev.ts) {
          statusBadge = '<span class="ps-card__badge caption-font">' + self.escapeHtml(ev.ts) + '</span>';
        }
        html += '<li class="predictive-search__list-item ps-card ps-card--event" role="option" aria-selected="false">';
        html += '<a href="/pages/levitation-presents#' + encodeURIComponent(ev.h) + '" class="ps-card__link" tabindex="-1">';
        html += '<div class="ps-card__media ps-card__media--poster">';
        if (ev.i) html += '<img src="' + ev.i + '" alt="' + self.escapeHtml(ev.t) + '" loading="lazy">';
        if (statusBadge) html += statusBadge;
        html += '</div>';
        html += '<div class="ps-card__body">';
        html += '<div class="ps-card__subtitle caption-font">' + self.escapeHtml(split.subtitle) + '</div>';
        html += '<h3 class="ps-card__title body-font">' + self.escapeHtml(split.title) + '</h3>';
        if (ev.df) html += '<div class="ps-card__meta caption-font">' + self.escapeHtml(ev.df) + '</div>';
        if (venueCity) html += '<div class="ps-card__meta caption-font">' + self.escapeHtml(venueCity) + '</div>';
        html += '</div></a></li>';
      });

      html += '</ul></div>';
      contentArea.insertAdjacentHTML('beforeend', html);

      var filterBar = this.predictiveSearchResults.querySelector('.search-filter-tabs');
      if (filterBar) {
        var evTab = filterBar.querySelector('[data-filter="events"]');
        if (evTab) {
          evTab.classList.remove('search-filter-tab--hidden');
          evTab.style.display = '';
        }
        this._updateFilterBarVisibility();
      }
    }
  }

customElements.define('predictive-search', PredictiveSearch);

